import { useEffect, useMemo, useState } from "react";
import ModalComponent from "components/modal";
import { useSharedState } from "state";
import { useParams } from "react-router-dom";
import {
  addCommentToDocument,
  approveDocumentRequest,
  deleteDocument,
  getCommentsForDocument,
  getDocumentApprovals,
  getDocumentById,
  getDocumentFileLink,
  getDocumentFileNames,
  getDocumentHtmlPreview,
  markDocumentTodoItemAsChecked,
  markDocumentTodoItemAsUnchecked,
  previewDocumentDocFile,
  restartDocumentRequest,
  uploadDocumentFiles,
} from "api/document";
import moment from "moment";
import DangerModal from "components/modal/danger";
import WarningModal from "components/modal/warning";
import { StatusEnum } from "types/dict";
import { CommentProps } from "types/comment";
import getUserInitials from "helpers/get-user-initials";
import { FileDetails, FilesResponse } from "types/file";
import { IconDownload, IconEye, IconFileText, IconFileTypeHtml, IconPlus } from "@tabler/icons-react";
import { DocumentFilters, DocumentProps } from "types/document";
import ForbiddenPage from "components/protected-route/forbidden";
import { PreviewFile } from "helpers/preview-file";
import StatusList from "components/details/status-list";

export interface ExpenseItem {
  name: string;
  quantity: number | undefined;
  price: number | undefined;
  sum: number | undefined;
  expense_item: string;
}

export const emptyExpenseRow: ExpenseItem = {
  name: "",
  quantity: undefined,
  price: undefined,
  sum: undefined,
  expense_item: "",
};

export interface StatusProps {
  name: string;
  position: string;
  status: number;
}

const deleteDocumentDanger = "Вы действительно хотите удалить данный договор?";
const rejectDocument = "Вы действительно хотите отклонить данный договор?";
const approveDocument = "Вы действительно хотите утвердить данный договор?";
const restartDocument = "Вы действительно хотите перезапустить данный договор?";

export default function DocumentDetail() {
  const { user, getDocumentsData, getDocumentsTableData, isAdmin, setCopyData, notify } = useSharedState();

  const { documentId, locationName } = useParams();

  const [documentItem, setDocumentItem] = useState<DocumentProps | undefined>(undefined);
  const [files, setFiles] = useState<FilesResponse | undefined>(undefined);

  const [isForbidden, setForbidden] = useState<boolean>(false);
  const [modalTab, setModalTab] = useState<"info" | "journal">("info");
  const [comments, setComments] = useState<CommentProps[] | undefined>(undefined);
  const [isFileLoading, setFileLoading] = useState<boolean>(false);

  const [newComment, setNewComment] = useState<string>("");
  const [isLeavingComment, setLeavingComment] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<StatusProps[]>([]);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);

  useEffect(() => {
    initDocument();
  }, [documentId]);

  const initDocument = async () => {
    try {
      await getDocumentData();
      await getStatuses();
      await getFilesData();
      await getCommentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      if (error?.response?.status === 403) {
        notify(err, "danger", "Отказано в доступе");
        setForbidden(true);
      }
    }
  };

  const openDocumentModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const cancelCommentLeave = () => {
    setLeavingComment(false);
    setNewComment("");
  };

  const changeTab = (tab: "info" | "journal") => {
    setModalTab(tab);
    cancelCommentLeave();
  };

  const getDocumentData = async () => {
    try {
      const res: DocumentProps = await getDocumentById(Number(documentId));
      setDocumentItem(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении договора");
      throw error;
    }
  };

  const getStatuses = async () => {
    try {
      const approvals: StatusProps[] = await getDocumentApprovals(documentId ? +documentId : 0);
      setStatuses(approvals);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении статусов");
    }
  };

  const getFilesData = async () => {
    try {
      const res: FilesResponse = await getDocumentFileNames(Number(documentId));
      setFiles(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файлов");
    }
  };

  const getCommentsData = async () => {
    try {
      const res: any = await getCommentsForDocument(Number(documentId));
      setComments(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении комментариев");
    }
  };

  const uploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const formData = new FormData();

      if (e.target.files?.length) {
        Array.from(e.target.files).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      await uploadDocumentFiles(Number(documentId), formData);

      notify("Файл добавлен", "success");
      getFilesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const onFileAction = async (file: FileDetails, action: "html_preview" | "preview" | "download") => {
    setFileLoading(true);
    try {
      if (action === "html_preview") {
        const res = await getDocumentHtmlPreview(Number(documentId), file.file_name);
        const htmlWindow = window.open("", "_blank");
        if (htmlWindow) {
          htmlWindow.document.open();
          htmlWindow.document.write(res);
          htmlWindow.document.close();
        }
      } else if (file.file_name.includes(".doc") && action === "preview") {
        const res = await previewDocumentDocFile(Number(documentId), file.file_name);
        PreviewFile(res, action);
      } else {
        const res = await getDocumentFileLink(Number(documentId), file);
        PreviewFile(res, action);
      }
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении ссылки");
    } finally {
      setFileLoading(false);
    }
  };

  const sortedStatuses = useMemo(() => {
    const completedStatuses = [...statuses].filter((s) => s.status === 2);
    const rejectedStatuses = [...statuses].filter((s) => s.status === 3);
    const currentStatuses = [...statuses].filter((s) => s.status === 1);
    const nullStatuses = [...statuses].filter((s) => s.status === null);

    return [...completedStatuses, ...rejectedStatuses, ...currentStatuses, ...nullStatuses];
  }, [statuses]);

  const isDocumentActive = useMemo(() => {
    return documentItem && documentItem.status !== StatusEnum.COMPLETED && documentItem.status !== StatusEnum.REJECTED
      ? true
      : false;
  }, [documentItem]);

  const isDocumentDeleteable = useMemo(() => {
    return documentItem && documentItem.status !== StatusEnum.COMPLETED && isAdmin ? true : false;
  }, [documentItem, isAdmin]);

  const isDocumentApprovable = useMemo(() => {
    if (documentItem && documentItem.pending_approver_id === user.result.user_id && isDocumentActive) return true;
    return false;
  }, [documentItem, user, isDocumentActive]);

  const isDocumentExecuting = useMemo(() => {
    if (
      documentItem &&
      documentItem.pending_action_type === "execute" &&
      documentItem.status === StatusEnum.IN_PROGRESS
    )
      return true;
    return false;
  }, [documentItem]);

  const isDocumentCopyable = useMemo(() => {
    return documentItem && documentItem.status === StatusEnum.COMPLETED;
  }, [documentItem]);

  const isDocumentRestartable = useMemo(() => {
    return documentItem && documentItem.user_id === user.result.user_id && documentItem.status === StatusEnum.REJECTED;
  }, [documentItem, user]);

  const isFooterVisible = useMemo(() => {
    return isDocumentDeleteable || isDocumentApprovable || isDocumentCopyable || isDocumentRestartable;
  }, [isDocumentDeleteable, isDocumentApprovable, isDocumentCopyable, isDocumentRestartable]);

  const isChecklistAvailable = useMemo(() => {
    return (
      documentItem &&
      (documentItem.status === StatusEnum.COMPLETED || documentItem.pending_action_type === "execute") &&
      documentItem.todo_items.length
    );
  }, [documentItem]);

  const isExecutingDisabled = useMemo(() => {
    return (
      documentItem &&
      documentItem.user_id === user.result.user_id &&
      documentItem.pending_action_type === "execute" &&
      documentItem.todo_items.some((item) => !!!item.is_completed)
    );
  }, [documentItem, user]);

  const onCheckChange = async (todoItemId: number, check: boolean) => {
    try {
      setFormLoading(true);

      switch (check) {
        case true:
          await markDocumentTodoItemAsChecked(todoItemId);
          break;

        case false:
          await markDocumentTodoItemAsUnchecked(todoItemId);
          break;

        default:
          break;
      }

      notify("Чек-лист отмечен", "success");
      getDocumentData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при отметке чек-листа");
    } finally {
      setFormLoading(false);
    }
  };

  const onDocumentDelete = async () => {
    openDocumentModal();
    setFormLoading(true);

    try {
      await deleteDocument(documentItem?.document_request_id || 0);

      setFormSubmitted(true);
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getDocumentsData(undefined, filters);
      await getDocumentsTableData(undefined, filters);
      notify("Договор удалён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении договора");
    } finally {
      setFormLoading(false);
    }
  };

  const onDocumentSubmit = async (approved: boolean) => {
    openDocumentModal();
    setFormLoading(true);

    try {
      await approveDocumentRequest(documentItem?.document_request_id || 0, approved);

      await initDocument();
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getDocumentsData(undefined, filters);
      await getDocumentsTableData(undefined, filters);
      notify(approved ? "Договор утверждён" : "Договор отклонён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", approved ? "Ошибка при утверждении договора" : "Ошибка при отклонении договора");
    } finally {
      setFormLoading(false);
    }
  };

  const onCommentLeave = async () => {
    setFormLoading(true);

    try {
      await addCommentToDocument(Number(documentId) || 0, newComment);

      cancelCommentLeave();
      await getCommentsData();
      notify("Комментарий сохранён", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении комментария");
    } finally {
      setFormLoading(false);
    }
  };

  const onDocumentCopy = () => {
    if (documentItem) {
      setCopyData(documentItem);
      setShowCopyModal(true);
    }
  };

  const onDocumentRestart = async () => {
    openDocumentModal();
    setFormLoading(true);

    try {
      await restartDocumentRequest(Number(documentId) || 0);

      await initDocument();
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getDocumentsData(undefined, filters);
      await getDocumentsTableData(undefined, filters);
      notify("Заявка перезапущена", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при перезапуске заявки");
    } finally {
      setFormLoading(false);
    }
  };

  const detailsHeader = (
    <ul className="nav nav-tabs card-header-tabs nav-fill" data-bs-toggle="tabs" role="tablist">
      <li className="nav-item" role="presentation">
        <div
          className="nav-link active cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="true"
          role="tab"
          onClick={() => changeTab("info")}
        >
          Детали договора
        </div>
      </li>
      <li className="nav-item" role="presentation">
        <div
          className="nav-link cursor-pointer"
          style={{ "--tblr-card-bg": "white" } as React.CSSProperties}
          data-bs-toggle="tab"
          aria-selected="false"
          role="tab"
          onClick={() => changeTab("journal")}
        >
          Журнал
        </div>
      </li>
    </ul>
  );

  const detailsBody = (
    <div className="row">
      <div className="col-lg-3 mb-3">
        <StatusList statusList={statuses} />

        {isChecklistAvailable && (
          <div className="px-4 py-2">
            <h3 className="datagrid-title mb-2">Чек-лист</h3>
            <div className="card-body mb-3">
              {documentItem?.todo_items.map((todo, index) => (
                <label className="form-check" key={index}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={todo.is_completed}
                    onChange={(e) => onCheckChange(todo.id, e.target.checked)}
                    disabled={isFormLoading || !isDocumentApprovable || documentItem.user_id !== user.result.user_id}
                  />
                  <span className="form-check-label">{todo.title}</span>
                </label>
              ))}
            </div>

            {isDocumentApprovable && documentItem?.user_id === user.result.user_id && (
              <div className="alert alert-primary m-0" style={{ padding: "7px 16px" }} role="alert">
                <div className="text-secondary">Для исполнения заявки отметьте весь чек-лист к исполнению</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="col-lg-9 mb-3">
        <div className="datagrid p-4 rounded-2xl border border-[#EFEFEF] mb-4">
          <div className="datagrid-item">
            <div className="datagrid-title">Контрагент</div>
            <div className="datagrid-content">{documentItem?.contragent || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Филиал</div>
            <div className="datagrid-content">{documentItem?.choosen_location || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Номер договора</div>
            <div className="datagrid-content">{documentItem?.lease_number || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Шаблон</div>
            <div className="datagrid-content">{documentItem?.template_name || "-"}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Период действия договора</div>
            <div className="datagrid-content">
              {moment(documentItem?.contract_start_date).format("DD.MM.YYYY") || "-"} -{" "}
              {moment(documentItem?.contract_end_date).format("DD.MM.YYYY") || "-"}
            </div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">Создатель</div>
            <div className="datagrid-content">{documentItem?.creator_name || "-"}</div>
          </div>
          <div className="datagrid-item" style={{ gridColumn: "1 / -1" }}>
            <div className="datagrid-title">Информация по договору</div>
            <div className="datagrid-content">{documentItem?.description || "-"}</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl border border-[#EFEFEF]">
          <div className="flex justify-between mb-3 datagrid-title font-semibold text-[#18181B]">
            Файлы
            {isFooterVisible && (
              <label
                htmlFor="new_file"
                className="flex items-center gap-1 pl-3 pr-[10px] py-1 rounded-lg text-sm font-semibold text-[#004369] bg-[#E5ECF0] cursor-pointer"
              >
                Добавить <IconPlus />
                <input type="file" id="new_file" onChange={uploadFiles} multiple hidden />
              </label>
            )}
          </div>

          {files?.file_details.length ? (
            <div className="flex flex-col gap-3">
              {files.file_details.map((file, index) => (
                <div key={index} className="flex items-center gap-3 p-4 rounded-2xl border border-[#EFEFEF]">
                  <div className="flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-[#E5ECF0]">
                    <IconFileText />
                  </div>

                  <div className="flex items-center justify-between gap-3 grow">
                    <div className="d-block text-truncate mt-n1" title={file.file_name}>
                      {file.file_name}
                    </div>

                    <div className="d-block text-truncate mt-n1">
                      {moment(file.saved_datetime).add(5, "hours").format("DD.MM.YY HH:mm") || "-"}
                    </div>
                  </div>

                  <div className="col-auto btn-list flex-nowrap">
                    {!!file.file_name.includes(".doc") && (
                      <button
                        type="button"
                        title="Просмотреть"
                        className="btn btn-icon btn-ghost-primary d-flex gap-2"
                        onClick={() => onFileAction(file, "html_preview")}
                        disabled={isFileLoading}
                      >
                        {isFileLoading ? (
                          <div className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          <IconFileTypeHtml className="icon" />
                        )}
                      </button>
                    )}
                    {!!!file.file_name.includes(".xls") && !!!file.file_name.includes(".csv") && (
                      <button
                        type="button"
                        title="Просмотреть"
                        className="btn btn-icon btn-ghost-primary d-flex gap-2"
                        onClick={() => onFileAction(file, "preview")}
                        disabled={isFileLoading}
                      >
                        {isFileLoading ? (
                          <div className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          <IconEye className="icon" />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      title="Скачать"
                      className="btn btn-icon btn-ghost-primary d-flex gap-2"
                      onClick={() => onFileAction(file, "download")}
                      disabled={isFileLoading}
                    >
                      {isFileLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <IconDownload className="icon" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>Нет файлов</div>
          )}
        </div>
      </div>
    </div>
  );

  const journalBody = (
    <div className="row">
      <div className="col-lg-12">
        <div className="comment-list">
          {comments?.map((comment, index) => (
            <div className="comment-item" key={index}>
              <div className="comment-body">
                <div className="flex items-center gap-3">
                  <div className="user-avatar flex items-center justify-center w-10 h-10 text-sm leading-4 font-medium rounded-full bg-[#FAFAFA]">
                    {getUserInitials(comment.user_name) || "-"}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="text-sm leading-[21px] font-semibold text-[#18181B]">{comment.user_name}</div>
                    <div className="text-xs leading-[18px] font-medium text-[#18181B]">
                      {moment(comment.comment_date).format("DD.MM.YYYY HH:mm")}
                    </div>
                  </div>
                </div>

                <div className="pl-[52px] text-sm leading-[21px] font-medium text-[#18181B]">{comment.logs}</div>
              </div>
              <div className="divider"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const detailsFooter = (
    <>
      {isDocumentCopyable && (
        <button className="btn btn-primary d-flex gap-2" onClick={onDocumentCopy} disabled={isFormLoading}>
          Копировать
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isDocumentRestartable && (
        <button
          className="btn btn-primary d-flex gap-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-restart-document"
          disabled={isFormLoading}
        >
          Перезапустить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isDocumentDeleteable && (
        <button
          className="btn btn-ghost-danger d-flex gap-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-delete-document"
          disabled={isFormLoading}
        >
          Удалить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      {isDocumentApprovable && (
        <>
          {!isDocumentExecuting && (
            <button
              className="btn btn-ghost-primary d-flex gap-2"
              data-bs-toggle="modal"
              data-bs-target="#modal-reject-document"
              disabled={isFormLoading}
            >
              Отклонить
              {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
            </button>
          )}

          <button
            className="btn btn-primary d-flex gap-2"
            data-bs-toggle="modal"
            data-bs-target="#modal-approve-document"
            disabled={isFormLoading || isExecutingDisabled}
          >
            Утвердить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      )}
    </>
  );

  const journalFooter = (
    <>
      {isLeavingComment ? (
        <>
          <input
            className="form-control rounded-2xl"
            style={{ width: 0, flexGrow: 1 }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newComment && !isFormLoading && onCommentLeave()}
            disabled={isFormLoading}
          />

          <button className="btn btn-ghost-primary d-flex gap-2" onClick={cancelCommentLeave} disabled={isFormLoading}>
            Отмена
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>

          <button
            className="btn btn-primary d-flex gap-2"
            onClick={onCommentLeave}
            disabled={!newComment || isFormLoading}
          >
            Сохранить
            {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
          </button>
        </>
      ) : (
        <button
          className="btn btn-primary d-flex gap-2"
          onClick={() => setLeavingComment(true)}
          disabled={isFormLoading}
        >
          Добавить комментарий
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}
    </>
  );

  return (
    <>
      <ModalComponent
        header={detailsHeader}
        body={isForbidden ? <ForbiddenPage /> : modalTab === "info" ? detailsBody : journalBody}
        footer={
          isFooterVisible && modalTab === "info"
            ? detailsFooter
            : isFooterVisible && modalTab === "journal"
            ? journalFooter
            : undefined
        }
        alignHeaderToBottom
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted || showCopyModal}
        shouldModalNavigate={
          showCopyModal
            ? `/documents/${documentItem?.choosen_location}/create`
            : `/documents/${documentItem?.choosen_location}`
        }
      />

      <DangerModal
        modal_id="modal-delete-document"
        dangerDescription={deleteDocumentDanger}
        cancel={openDocumentModal}
        confirm={onDocumentDelete}
      />
      <WarningModal
        modal_id="modal-reject-document"
        warningDescription={rejectDocument}
        cancel={openDocumentModal}
        confirm={() => onDocumentSubmit(false)}
      />
      <WarningModal
        modal_id="modal-approve-document"
        warningDescription={approveDocument}
        cancel={openDocumentModal}
        confirm={() => onDocumentSubmit(true)}
      />
      <WarningModal
        modal_id="modal-restart-document"
        warningDescription={restartDocument}
        cancel={openDocumentModal}
        confirm={() => onDocumentRestart()}
      />
    </>
  );
}
